import { useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { getUserOptions, useUsers } from '@/features/user/user.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Lock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import useAuth from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { RoomDateColumn } from '@/components/datatable/columns/rooms/rooms-columns';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import UserCreateUpdateModal from '@/components/modals/user-create-update-modal';
import Tabs from '@/components/tabs/tabs-custom';

import ParentsTab from './-parents-tab';

const settingsRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  type: z.enum(['user', 'parent']).optional(),
  user: z.number().optional(),
  parent: z.number().optional(),
});

const VISIBLE_TO_ROLES = [UserRoles.Root, UserRoles.Admin, UserRoles.Secretary];

export const Route = createFileRoute('/_app/(settings)/settings/')({
  validateSearch: zodSearchValidator(settingsRouteSchema),
  component: SettingTable,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (!VISIBLE_TO_ROLES.includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function SettingTable() {
  const { isRole } = useAuth();
  const isSecretary = isRole(UserRoles.Secretary);
  const [tab, setTab] = useState(isSecretary ? 'parents' : 'users');

  const tabs = [
    ...(!isSecretary
      ? [
          {
            label: 'Διαχειριστές',
            value: 'users',
            content: (
              <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
                <UsersTabHeader />
                <UserTable />
              </div>
            ),
          },
        ]
      : []),
    {
      label: 'Γονείς',
      value: 'parents',
      content: <ParentsTab />,
    },
  ];

  return <Tabs tab={tab} onChangeTab={setTab} className='pt-4' tabs={tabs} />;
}

function UsersTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['settings', 'table', 'common']);

  const navigateToSettings = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={navigateToSettings} size='default'>
          <User strokeWidth={4} className='h-6 w-6' />
          {t('create', { ns: 'common' })}
        </Button>
      </div>
      <UserCreateUpdateModal />
    </div>
  );
}

function UserTable() {
  const { t } = useTranslation(['room', 'common']);

  const { data = [], isLoading } = useUsers();

  return (
    <DataTableSimple
      data={data}
      columns={['id', 'name', 'role', 'createdAt', 'updatedAt']}
      isLoading={isLoading}
      columnsLabels={{
        id: t('serial_id', { ns: 'table' }),
        name: toCapitalize(t('name', { ns: 'table' })),
        role: toCapitalize(t('role', { ns: 'table' })),
        createdAt: t('created_at', { ns: 'table' }),
        updatedAt: t('updated_at', { ns: 'table' }),
      }}
      customRender={{
        createdAt: (row) => <RoomDateColumn date={row.getValue('createdAt')} />,
        updatedAt: (row) => <RoomDateColumn date={row.getValue('updatedAt')} />,
        role: (row) => (
          <div className='h-6 w-6 rounded-lg'>
            {row.getValue('role') === 'root' ? 'System Role' : row.getValue('role')}
          </div>
        ),
      }}
      extraColumns={[
        {
          id: 'actions',
          size: 50,
          cell: ({ row }) => {
            const userRole = row.getValue('role'); // Replace with actual way of getting user role
            const isRoot = userRole === UserRoles.Root;

            const actions = !isRoot ? [] : [];

            return !isRoot ? (
              <DataTableRowActions row={row} menuItems={actions} />
            ) : (
              <Lock size={16} />
            );
          },
        },
      ]}
      className='flex h-full flex-col overflow-y-auto'
    />
  );
}
